/**
 * Theme + direction helpers.
 *
 * The actual Tailwind tokens are CSS custom properties scoped under
 * `.theme-dark`, `.theme-hc`, etc. (see `styles/tokens.css`). These helpers
 * apply / read the active theme via `<html>` className + `dir` attribute.
 *
 * Operator Console defaults to dark; the high-contrast theme is opt-in via
 * a user preference toggle and may be enforced for control-room shifts.
 */

export type ThemeName = 'light' | 'dark' | 'hc';
export type Direction = 'ltr' | 'rtl';

const THEME_CLASSES: Record<ThemeName, string | null> = {
  light: null,
  dark: 'theme-dark',
  hc: 'theme-hc',
};

export function applyTheme(theme: ThemeName, root: HTMLElement = document.documentElement): void {
  for (const cls of Object.values(THEME_CLASSES)) {
    if (cls) root.classList.remove(cls);
  }
  const next = THEME_CLASSES[theme];
  if (next) root.classList.add(next);
  root.dataset.theme = theme;
}

/** Theme-toggle order shared by every shell: dark → high contrast → light → dark. */
export function nextTheme(theme: ThemeName): ThemeName {
  return theme === 'dark' ? 'hc' : theme === 'hc' ? 'light' : 'dark';
}

export function applyDirection(
  dir: Direction,
  lang: string | undefined,
  root: HTMLElement = document.documentElement,
): void {
  root.setAttribute('dir', dir);
  if (lang) root.setAttribute('lang', lang);
}
